import { ShieldCheck } from 'lucide-react'

/**
 * The admin desk's front door.
 *
 * This deliberately belongs to SignIn instead of the shared centred layout.
 * Provider invitations and guest teaching links are short utility flows. The
 * desk sign-in is the product's daily entry point and needs its own hierarchy,
 * artwork and responsive treatment without changing those other surfaces.
 */
export function AdminSignInLayout({ children }: { children: React.ReactNode }) {
  return (
    <main className="fixed inset-0 grid h-dvh max-h-dvh overflow-hidden bg-canvas lg:grid-cols-[minmax(0,1.08fr)_minmax(480px,0.92fr)]">
      <section
        aria-label="High Impact administration desk"
        className="relative hidden min-h-0 overflow-hidden border-r border-white/8 bg-[#2a201e] text-white lg:flex lg:flex-col"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -left-24 -top-28 size-72 rounded-full border border-white/8"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -left-8 -top-12 size-44 rounded-full border border-white/8"
        />

        <div className="relative z-10 flex h-full min-h-0 flex-col px-8 py-5 xl:px-12">
          <div className="flex items-center gap-3">
            <img
              src="/brand/high-impact-logo-128.png"
              width={44}
              height={44}
              alt=""
              className="object-contain drop-shadow-[0_12px_20px_rgba(0,0,0,0.24)]"
            />
            <div>
              <p className="text-[15px] font-semibold tracking-[-0.01em] text-white">High Impact</p>
              <p className="mt-0.5 text-[11px] font-medium uppercase tracking-[0.18em] text-white/55">
                Administration desk
              </p>
            </div>
          </div>

          <div className="mt-4 max-w-[620px]">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#f9b95c]">
              One clear view
            </p>
            <h1 className="mt-2 max-w-[590px] text-[clamp(2rem,2.6vw,3rem)] font-semibold leading-[1.12] tracking-[-0.035em] text-white">
              Run High Impact from one clear view.
            </h1>
            <p className="mt-3 max-w-[560px] text-[clamp(0.95rem,1.2vw,1.08rem)] leading-[1.4] text-white/67">
              Members, professionals, bookings, learning and payments come together in one carefully
              managed place.
            </p>
          </div>

          <figure className="relative mt-4 flex min-h-0 flex-1 items-center justify-start">
            <img
              src="/illustrations/authentication/admin-sign-in.webp"
              alt=""
              className="h-auto max-h-full w-auto max-w-full rounded-[20px] object-contain"
            />
          </figure>

          <div className="mt-3 flex items-center gap-3 text-[13px] text-white/68">
            <span className="grid size-8 place-items-center rounded-full bg-white/8 text-[#f9b95c]">
              <ShieldCheck aria-hidden className="size-4" />
            </span>
            <span>Private access for the High Impact team</span>
          </div>
        </div>
      </section>

      <section aria-label="Account access" className="relative min-h-0 min-w-0">
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -right-30 -top-30 size-[360px] rounded-full bg-coral-soft opacity-70 blur-3xl" />
        </div>
        <div className="relative flex h-full min-h-0 min-w-0 overflow-x-hidden overflow-y-auto overscroll-contain px-4 py-4 sm:px-10 sm:py-8 lg:px-12 xl:px-20">
          <div className="relative z-10 mx-auto my-auto w-full min-w-0 max-w-[480px] shrink-0 py-2">
            <div className="mb-5 flex items-center gap-3 lg:hidden">
              <img
                src="/brand/high-impact-logo-128.png"
                width={42}
                height={42}
                alt=""
                className="object-contain drop-shadow-[0_8px_14px_rgba(47,36,34,0.18)]"
              />
              <div>
                <p className="text-card font-semibold text-ink">High Impact</p>
                <p className="text-micro mt-0.5 font-medium uppercase tracking-[0.16em] text-ink-muted">
                  Administration desk
                </p>
              </div>
            </div>

            {children}

            <div className="mt-7 flex items-start justify-center gap-2 text-center text-caption leading-5 text-ink-muted">
              <ShieldCheck aria-hidden className="mt-0.5 size-4 shrink-0 text-teal-text" />
              <p>Protected team access. Your session is secured and automatically expires.</p>
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}
